import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_STYLES = {
  HIGH: 'bg-emerald-500/20 text-emerald-300',
  MEDIUM: 'bg-amber-500/20 text-amber-300',
  LOW: 'bg-slate-600/30 text-slate-400',
};

const TierBadge = ({ tier }) => (
  <span
    data-testid={`tier-${tier?.toLowerCase()}`}
    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${TIER_STYLES[tier] || TIER_STYLES.LOW}`}
  >
    {tier}
  </span>
);

const EmptyState = () => (
  <div
    data-testid="abh-attribution-empty"
    className="bg-slate-900/60 border border-slate-800 rounded-xl p-8 text-center"
  >
    <p className="text-slate-300 font-medium">No performance data yet.</p>
    <p className="text-slate-500 text-sm mt-1.5 max-w-lg mx-auto">
      Run your first ABH campaign and connect Meta API to start building intelligence.
    </p>
  </div>
);

const Table = ({ testid, columns, rows, sortState, onSort, emptyLabel }) => {
  if (!rows.length) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="text-center text-slate-500 text-sm py-6"
      >
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table
        data-testid={testid}
        className="w-full text-sm text-left border-collapse"
      >
        <thead>
          <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={c.sortable ? () => onSort(c.key) : undefined}
                className={`py-2 px-3 font-normal ${c.sortable ? 'cursor-pointer hover:text-slate-200' : ''}`}
              >
                {c.label}
                {c.sortable && sortState?.key === c.key && (
                  <span className="ml-1">{sortState.dir === 'asc' ? '↑' : '↓'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={idx}
              className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"
            >
              {columns.map((c) => (
                <td key={c.key} className="py-2 px-3 text-slate-200">
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const FilterBar = ({ filters, setFilters }) => (
  <div
    data-testid="abh-attribution-filters"
    className="flex flex-wrap gap-3 items-center bg-slate-900/40 border border-slate-800 rounded-lg p-3 mb-4"
  >
    <label className="text-xs text-slate-400">Channel</label>
    <select
      data-testid="filter-channel"
      value={filters.channel || ''}
      onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
      className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
    >
      <option value="">All</option>
      <option value="META">Meta</option>
      <option value="TIKTOK">TikTok</option>
    </select>
    <label className="text-xs text-slate-400">Production</label>
    <select
      data-testid="filter-production-type"
      value={filters.production_type || ''}
      onChange={(e) => setFilters({ ...filters, production_type: e.target.value })}
      className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
    >
      <option value="">All</option>
      <option value="VEO">Veo</option>
      <option value="VE">VE</option>
      <option value="UGC">UGC</option>
    </select>
    <label className="text-xs text-slate-400">Min confidence</label>
    <select
      data-testid="filter-min-tier"
      value={filters.min_tier || ''}
      onChange={(e) => setFilters({ ...filters, min_tier: e.target.value })}
      className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
    >
      <option value="">Any</option>
      <option value="MEDIUM">Medium+</option>
      <option value="HIGH">High only</option>
    </select>
    <label className="text-xs text-slate-400">Date from</label>
    <input
      data-testid="filter-date-from"
      type="date"
      value={filters.date_from || ''}
      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
      className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
    />
    <label className="text-xs text-slate-400">to</label>
    <input
      data-testid="filter-date-to"
      type="date"
      value={filters.date_to || ''}
      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
      className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1 border border-slate-700"
    />
    {(filters.channel || filters.production_type || filters.min_tier || filters.date_from || filters.date_to) && (
      <button
        data-testid="filter-clear"
        onClick={() => setFilters({})}
        className="text-xs text-slate-400 hover:text-slate-200"
      >
        Clear
      </button>
    )}
  </div>
);

const useSortedRows = (rows, sortState) => useMemo(() => {
  if (!sortState?.key) return rows;
  const sorted = [...rows].sort((a, b) => {
    const va = a[sortState.key];
    const vb = b[sortState.key];
    if (va === vb) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp = va < vb ? -1 : 1;
    return sortState.dir === 'asc' ? cmp : -cmp;
  });
  return sorted;
}, [rows, sortState]);

const ABHAttributionPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [angles, setAngles] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [ctas, setCtas] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [angleSort, setAngleSort] = useState({ key: 'avg_cpa', dir: 'asc' });
  const [hookSort, setHookSort] = useState({ key: 'avg_thumb_stop_rate', dir: 'desc' });
  const [ctaSort, setCtaSort] = useState({ key: 'avg_cvr', dir: 'desc' });

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [a, h, c, p] = await Promise.all([
        axios.get(`${API}/abh/attribution/angles${qs}`),
        axios.get(`${API}/abh/attribution/hooks${qs}`),
        axios.get(`${API}/abh/attribution/ctas${qs}`),
        axios.get(`${API}/abh/attribution/pillars${qs}`),
      ]);
      setAngles(a.data?.rows || []);
      setHooks(h.data?.rows || []);
      setCtas(c.data?.rows || []);
      setPillars(p.data?.rows || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load attribution data');
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => { load(); }, [load]);

  const totalRecords = angles.length + hooks.length + ctas.length;
  const noMetaData = !loading && !err && totalRecords === 0;

  const handleSort = (setter) => (key) => {
    setter((prev) => ({
      key,
      dir: prev?.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedAngles = useSortedRows(angles, angleSort);
  const sortedHooks = useSortedRows(hooks, hookSort);
  const sortedCtas = useSortedRows(ctas, ctaSort);

  const startSessionFrom = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, v); });
    // Preserve channel from filters if set
    if (filters.channel && !qs.get('channel')) qs.set('channel', filters.channel);
    navigate(`/abh-engine?${qs.toString()}`);
  };

  const DuplicateButton = ({ row, kind }) => {
    if (row.confidence_tier === 'HIGH') {
      return (
        <button
          data-testid={`abh-duplicate-${kind}-${row.angle_id || row.hook_element_id || row.cta_element_id}`}
          onClick={() => startSessionFrom({
            angle_id: row.angle_id,
            hook_element_id: row.hook_element_id,
            hook_type: row.hook_type,
            cta_element_id: row.cta_element_id,
          })}
          className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-500"
        >
          Start session
        </button>
      );
    }
    return (
      <span
        title="Not enough data yet for confident replication. Build more history first."
        className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-500 cursor-not-allowed"
      >
        Start session
      </span>
    );
  };

  return (
    <div data-testid="abh-attribution-page" className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-white text-2xl font-semibold">ABH Attribution</h1>
          <p className="text-slate-400 text-sm mt-1">
            Which angles, hooks, and CTAs are winning — based on Meta performance data.
          </p>
        </div>
        <button
          data-testid="abh-attribution-refresh"
          onClick={load}
          className="text-sm px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} />

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : noMetaData ? (
        <EmptyState />
      ) : (
        <div className="space-y-6">
          {/* Angle performance */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <h2 className="text-white text-base font-medium mb-3">Angle performance</h2>
            <Table
              testid="abh-angle-table"
              rows={sortedAngles}
              sortState={angleSort}
              onSort={handleSort(setAngleSort)}
              emptyLabel="No angle performance data for this filter."
              columns={[
                { key: 'angle_name', label: 'Angle', sortable: true },
                { key: 'psychological_entry_point', label: 'Entry Point', sortable: true },
                { key: 'times_used', label: 'Used', sortable: true },
                { key: 'avg_thumb_stop_rate', label: 'Thumb Stop', sortable: true,
                  render: (r) => `${(r.avg_thumb_stop_rate * 100).toFixed(1)}%` },
                { key: 'avg_ctr', label: 'CTR', sortable: true,
                  render: (r) => `${(r.avg_ctr * 100).toFixed(2)}%` },
                { key: 'avg_cpa', label: 'CPA', sortable: true,
                  render: (r) => r.avg_cpa ? `$${r.avg_cpa.toFixed(2)}` : '—' },
                { key: 'avg_cvr', label: 'CVR', sortable: true,
                  render: (r) => `${(r.avg_cvr * 100).toFixed(2)}%` },
                { key: 'confidence_tier', label: 'Conf.',
                  render: (r) => <TierBadge tier={r.confidence_tier} /> },
                { key: 'action', label: '',
                  render: (r) => <DuplicateButton row={r} kind="angle" /> },
              ]}
            />
          </div>

          {/* Hook performance */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <h2 className="text-white text-base font-medium mb-3">Hook performance</h2>
            <Table
              testid="abh-hook-table"
              rows={sortedHooks}
              sortState={hookSort}
              onSort={handleSort(setHookSort)}
              emptyLabel="No hook performance data for this filter."
              columns={[
                { key: 'hook_text', label: 'Hook', sortable: false,
                  render: (r) => <span className="text-slate-300">{r.hook_text || '—'}</span> },
                { key: 'hook_type', label: 'Type', sortable: true },
                { key: 'angle_name', label: 'Angle', sortable: true },
                { key: 'times_used', label: 'Used', sortable: true },
                { key: 'avg_thumb_stop_rate', label: 'Thumb Stop', sortable: true,
                  render: (r) => `${(r.avg_thumb_stop_rate * 100).toFixed(1)}%` },
                { key: 'avg_hook_retention_3s', label: 'Retention 3s', sortable: true,
                  render: (r) => `${(r.avg_hook_retention_3s * 100).toFixed(1)}%` },
                { key: 'avg_ctr', label: 'CTR', sortable: true,
                  render: (r) => `${(r.avg_ctr * 100).toFixed(2)}%` },
                { key: 'confidence_tier', label: 'Conf.',
                  render: (r) => <TierBadge tier={r.confidence_tier} /> },
                { key: 'action', label: '',
                  render: (r) => <DuplicateButton row={r} kind="hook" /> },
              ]}
            />
          </div>

          {/* CTA performance */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <h2 className="text-white text-base font-medium mb-3">CTA performance</h2>
            <Table
              testid="abh-cta-table"
              rows={sortedCtas}
              sortState={ctaSort}
              onSort={handleSort(setCtaSort)}
              emptyLabel="No CTA performance data for this filter."
              columns={[
                { key: 'cta_text', label: 'CTA', sortable: false },
                { key: 'times_used', label: 'Used', sortable: true },
                { key: 'avg_ctr', label: 'CTR', sortable: true,
                  render: (r) => `${(r.avg_ctr * 100).toFixed(2)}%` },
                { key: 'avg_cvr', label: 'CVR', sortable: true,
                  render: (r) => `${(r.avg_cvr * 100).toFixed(2)}%` },
                { key: 'confidence_tier', label: 'Conf.',
                  render: (r) => <TierBadge tier={r.confidence_tier} /> },
                { key: 'action', label: '',
                  render: (r) => <DuplicateButton row={r} kind="cta" /> },
              ]}
            />
          </div>

          {/* Pillar summary */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <h2 className="text-white text-base font-medium mb-3">Pillar performance</h2>
            <div
              data-testid="abh-pillar-table"
              className="grid grid-cols-1 sm:grid-cols-3 gap-4"
            >
              {pillars.map((p) => (
                <div
                  key={p.pillar}
                  className="bg-slate-800/40 rounded-lg p-4"
                  data-testid={`pillar-${p.pillar.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-slate-300 font-medium">{p.pillar}</p>
                    <TierBadge tier={p.confidence_tier} />
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Creatives</span>
                      <span className="text-white">{p.creative_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Avg CPA</span>
                      <span className="text-white">{p.avg_cpa ? `$${p.avg_cpa.toFixed(2)}` : '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Avg CTR</span>
                      <span className="text-white">{(p.avg_ctr * 100).toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ABHAttributionPage;
